
import routerApp, { roundToPlaces } from "../app.module";
import { getCacheBustedTemplate } from "../app.route";

enum Unit {
  Litres = 'litres',
  CubicM = 'cubicMetres',
  Gallons = 'gallons',
  Barrels = 'barrels',
  Celcius = 'celcius',
  Faren = 'fahrenheit',
}

const VOLUME_M3 = {
  [Unit.Litres]: 0.001,
  [Unit.CubicM]: 1,
  [Unit.Gallons]: 0.003785411784,
  [Unit.Barrels]: 0.158987294928,
};

const COEFF_DICT = [
  { min: 0, max: 770, k0: 346.42278, k1: 0.43884, midRange: false },
  { min: 770, max: 777.99, k0: -0.0033612, k1: 2680.32, midRange: true },
  { min: 778, max: 838.99, k0: 594.5418, k1: 0.0, midRange: false },
  { min: 839, max: 1100, k0: 186.9696, k1: 0.48618, midRange: false },
];

const WEIGHT_FACTOR_DICT = [
  { min: 0.5, max: 0.5191, wcf: 0.99775 },
  { min: 0.5192, max: 0.5421, wcf: 0.99785 },
  { min: 0.5422, max: 0.5673, wcf: 0.99795 },
  { min: 0.5674, max: 0.595, wcf: 0.99805 },
  { min: 0.5951, max: 0.6255, wcf: 0.99815 },
  { min: 0.6256, max: 0.6593, wcf: 0.99825 },
  { min: 0.6594, max: 0.697, wcf: 0.99835 },
  { min: 0.6971, max: 0.7392, wcf: 0.99845 },
  { min: 0.7393, max: 0.7869, wcf: 0.99855 },
  { min: 0.787, max: 0.8411, wcf: 0.99865 },
  { min: 0.8412, max: 0.9034, wcf: 0.99875 },
  { min: 0.9035, max: 0.9756, wcf: 0.99885 },
  { min: 0.9757, max: 1.0604, wcf: 0.99895 },
  { min: 1.0605, max: 1.1, wcf: 0.99905 },
];

const TEMP_UNIT_CELCIUS = {
  name: Unit.Celcius,
  symbol: '°C'
}
const TEMP_UNIT_FAREN = {
  name: Unit.Faren,
  symbol: '°F'
}

routerApp.directive('conversionCalc', ['$document', ($document) => {
  return {
    templateUrl: getCacheBustedTemplate('pages/directives/conversion-calc.html'),
    link: (scope, element) => {
      scope.openCalculator = () => 
        $('#conversion-calc').modal('toggle');

      scope.closeCalculator = () => 
        $('#conversion-calc').modal('toggle');
      
      const dialog: HTMLElement = element[0].querySelector('.modal-dialog');
      const header: HTMLElement = element[0].querySelector('#calc-header');

      const dims = {
        startX: 0,
        startY: 0,
        x: 0,
        y: 0
      }

      dialog.style.position = 'relative';
      header.addEventListener('mousedown', (event: MouseEvent) => {
        event.preventDefault();
        dims.startX = event.pageX - dims.x;
        dims.startY = event.pageY - dims.y;
        $document.on('mousemove', mouseMove);
        $document.on('mouseup', mouseUp);
      })

      const mouseMove = (event: MouseEvent) => {
        dims.y = event.pageY - dims.startY;
        dims.x = event.pageX - dims.startX;

        dialog.style.top = dims.y + 'px';
        dialog.style.left = dims.x + 'px';
      }
      const mouseUp = () => {
        $document.off('mousemove', mouseMove);
        $document.off('mouseup', mouseUp);
      }

      scope.calculator = {};
      scope.calculator.hasSpeedSwapped = false;
      scope.calculator.conversionFactorType = [
        {
          id: 'vcf_wcf',
          label: 'VCF and WCF'
        },
        {
          id: 'vcf',
          label: 'VCF Only'
        },
        {
          id: 'none',
          label: 'None'
        }
      ]
      scope.calculator.selectedConversionFactorType = scope.calculator.conversionFactorType[0];
      
      scope.swapSpeed = () => {
        scope.calculator.hasSpeedSwapped = !scope.calculator.hasSpeedSwapped;
        const storeSpeedInput1 = scope.calculator.speedInput1;
        scope.calculator.speedInput1 = scope.calculator.speedInput2;
        scope.calculator.speedInput2 = storeSpeedInput1;
      }

      scope.copySpeedInput2 = () => {
        navigator.clipboard.writeText(scope.calculator.speedInput2);
      }

      scope.speedInputChanged = (index) => {
        const currInput = `speedInput${index}`;
        const updateInput = `speedInput${(index === 1) ? 2 : 1}`;
        scope.calculator[updateInput] = convertSpeed(scope.calculator[currInput], index);
      }

      const convertSpeed = (value, index) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return '';
        if (isInputKnots(index)) return Number((numValue * 0.51444).toFixed(5));
        if (!isInputKnots(index)) return Number((numValue * 1.94384).toFixed(5));
      }

      const isInputKnots = (index) => {
        const hasSpeedSwapped = scope.calculator.hasSpeedSwapped;
        if (hasSpeedSwapped && index === 2) return true;
        if (!hasSpeedSwapped && index === 1) return true;
        return false;
      }

      scope.calculator.selectedTempUnit = TEMP_UNIT_CELCIUS;

      scope.toggleTempUnit = () => {
        const selected = scope.calculator.selectedTempUnit;
        scope.calculator.selectedTempUnit =
          (selected.name ===  Unit.Celcius) ? TEMP_UNIT_FAREN : TEMP_UNIT_CELCIUS;
      }

      scope.calculator.volumeUnits = [
        {
          id: Unit.Litres,
          name: 'Litres',
          symbol: 'L'
        },
        {
          id: Unit.CubicM,
          name: 'Cubic metres',
          symbol: 'm³'
        },
        {
          id: Unit.Gallons,
          name: 'Gallons',
          symbol: 'gal'
        },
        {
          id: Unit.Barrels,
          name: 'Barrels',
          symbol: 'bbl'
        }
      ];
      scope.calculator.selectedVolumeUnit = scope.calculator.volumeUnits[0];

      scope.copyVolumeMassInput2 = () => {
        navigator.clipboard.writeText(scope.calculator.volumeMassInput2);
      }

      scope.calculator.hasVolumeMassSwapped = false;
      scope.swapVolumeMass = () => {
        scope.calculator.hasVolumeMassSwapped = !scope.calculator.hasVolumeMassSwapped;
        const storeVolumeMassInput1 = scope.calculator.volumeMassInput1;
        scope.calculator.volumeMassInput1 = scope.calculator.volumeMassInput2;
        scope.calculator.volumeMassInput2 = storeVolumeMassInput1;
      }

      scope.calculator.invalidDensity = false;

      scope.$watchGroup(
        [
          'calculator.avgTankTemp',
          'calculator.avgFuelDensity',
          'calculator.volumeMassInput1',
          'calculator.selectedVolumeUnit',
          'calculator.selectedTempUnit',
          'calculator.selectedConversionFactorType'
        ],
        ([temp, density, input, unit, _]) => {
          scope.calculator.invalidDensity = !isDensityRangeValid();
          scope.calculator.volumeMassInput2 = undefined;
          if (!density) return;
          const densityNorm = density / 1000;
          const wcf = getWcf(densityNorm);
          if(!wcf) return;

          const vcf = calculateVcf(temp, density);

          if (!input) return;
          const hasSwapped = scope.calculator.hasVolumeMassSwapped;
          const result = hasSwapped
            ? (input / (densityNorm * vcf * wcf)) / VOLUME_M3[unit.id]
            : VOLUME_M3[unit.id] * input * densityNorm * vcf * wcf;

          scope.calculator.volumeMassInput2 = roundToPlaces(result, 5);
        },
      );

      const getKCoefficients = (density: number) => {
        const match = COEFF_DICT.find(a =>
          (density >= a.min && density <= a.max)
        );
        return {
          k0: match.k0, k1: match.k1, midRange: match.midRange
        }
      };

      const calcCoeff = (
        k0: number,
        k1: number,
        density: number,
        midRange: boolean
      ) => {
        if(midRange) return k0 + (k1 / (density **2));
        return ((k0 + (k1 * density)) / (density **2));
      };

      const getDeltaTemp = (tempValue: number) => {
        const isF = scope.calculator.selectedTempUnit.name === Unit.Faren;
        const getCelcius = (t: number) => (t - 32) * (5 / 9);
        const temp = isF ? getCelcius(tempValue) : tempValue;
        return temp - 15;
      }

      const DEFAULT_RETURN_VALUES = {
        vcf: 1,
        wcf: 1,
      }

      const DENSITY_LIMITS = {
        lower: 500,
        upper: 1100,
      }

      const calculateVcf = (temp: number, density: number) => {
        if (!isCorrectionFactorIncluded('vcf'))
          return DEFAULT_RETURN_VALUES.vcf;

        const deltaT = getDeltaTemp(temp ?? 15);
        const { k0, k1, midRange } = getKCoefficients(density);
        const a = calcCoeff(k0, k1, density, midRange);
        const b = -a * deltaT * (1 + 0.8 * a * deltaT);
        return roundToPlaces(Math.exp(b), 4);
      };

      const getWcf = (densityNorm: number) => {
        if (!isCorrectionFactorIncluded('wcf'))
          return DEFAULT_RETURN_VALUES.wcf;

        const dRound = roundToPlaces(densityNorm, 5);
        const match = WEIGHT_FACTOR_DICT.find(a =>
          dRound >= a.min && dRound <= a.max
        );
        if (!match) return;

        return match.wcf;
      };

      const isCorrectionFactorIncluded = (type: string) => {
        const selectedType = scope.calculator.selectedConversionFactorType.id;
        if (selectedType.includes(type)) return true;
        return false;
      }

      const isDensityRangeValid = () => {
        const density = scope.calculator.avgFuelDensity;
        const volumeMassInput = scope.calculator.volumeMassInput1;
        if (!volumeMassInput) return true;
        if (!density) return false;
        if (density < DENSITY_LIMITS.lower) return false;
        if (density > DENSITY_LIMITS.upper) return false;
        return true;
      }
    }
  }
}])

